"use client";

import { getCity } from "@/content/data/cities";
import { getService } from "@/content/data/services";
import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/track";

interface Props {
  /** City slug from data/cities.ts. */
  city: string;
  /** Service slug from data/services.ts. */
  service: string;
  /** Optional variant for visual rotation. */
  variant?: "aurora" | "onyx" | "sunset";
}

function slugHost(citySlug: string, serviceSlug: string): string {
  const sub = citySlug.replace(/-/g, "");
  const path = serviceSlug.split("-").slice(0, 2).join("-");
  return `${sub}.dimensione4.it/${path}`;
}

/**
 * Awwwards-grade browser-chrome mockup, personalized per (city, service).
 *
 * Layered composition: browser chrome → navbar → hero block with kpi chips →
 * feature grid → footer strip. Animated gradient aurora behind. Pure CSS + SVG,
 * no asset dependencies, fully responsive.
 */
export function DemoSitePreview({ city, service, variant = "aurora" }: Props) {
  const c = getCity(city);
  const s = getService(service);
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            track.demoPreviewOpen({ service, city });
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, service, city]);

  if (!c || !s) return null;

  const palette: Record<string, { primary: string; accent: string; bg: string }> = {
    aurora: {
      primary: "hsl(179, 80%, 50%)",
      accent: "hsl(45, 95%, 60%)",
      bg: "linear-gradient(135deg, #04070c 0%, #071018 55%, #0a1a22 100%)",
    },
    onyx: {
      primary: "hsl(210, 100%, 65%)",
      accent: "hsl(290, 85%, 65%)",
      bg: "linear-gradient(135deg, #06080f 0%, #0a0e18 55%, #11162a 100%)",
    },
    sunset: {
      primary: "hsl(25, 95%, 60%)",
      accent: "hsl(340, 85%, 65%)",
      bg: "linear-gradient(135deg, #0a0407 0%, #15080e 55%, #22101a 100%)",
    },
  };
  const p = palette[variant];

  const kpis = [
    { label: "Conversion", value: "+2.8x" },
    { label: "LCP", value: "1.2s" },
    { label: "Lead qualif.", value: "+180%" },
    { label: "Tempo risposta", value: "24h" },
  ];

  return (
    <figure
      ref={ref}
      className="not-prose my-12 overflow-hidden rounded-2xl border border-white/10 shadow-[0_60px_100px_-40px_rgba(0,0,0,0.6)]"
      aria-label={`Esempio di sito ${s.name} per ${c.name}`}
      style={{
        transition: "transform 700ms cubic-bezier(0.22,1,0.36,1)",
        transform: inView ? "translateY(0)" : "translateY(8px)",
      }}
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-3 bg-[#0a0d14] px-5 py-3 border-b border-white/10">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[#ff5f56]" aria-hidden />
          <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" aria-hidden />
          <span className="w-3 h-3 rounded-full bg-[#27c93f]" aria-hidden />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="flex items-center gap-2 max-w-[420px] truncate rounded-lg bg-white/[0.04] border border-white/5 px-3.5 py-1.5 text-[11px] font-mono text-white/60">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 2a10 10 0 0 0-3.162 19.487C8.352 20.05 8 18.53 8 17v-1c0-.4-.14-.78-.39-1.083A7.994 7.994 0 0 1 12 4a8 8 0 0 1 8 8c0 4.418-3.582 8-8 8h-.5v2H12a10 10 0 0 0 0-20Z"
                fill="currentColor"
              />
            </svg>
            https://{slugHost(c.slug, s.slug)}
          </div>
        </div>
        <span className="w-3 h-3" aria-hidden />
      </div>

      {/* Faux site */}
      <div
        className="relative"
        style={{
          background: p.bg,
          color: "#f1f5f9",
        }}
      >
        {/* Aurora blob 1 */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 -left-24 w-[520px] h-[420px] rounded-full blur-[100px] opacity-60"
          style={{ background: p.primary }}
        />
        {/* Aurora blob 2 */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -right-16 w-[460px] h-[360px] rounded-full blur-[110px] opacity-35"
          style={{ background: p.accent }}
        />
        {/* Grid backdrop */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />

        {/* Mock nav */}
        <div className="relative flex items-center justify-between px-8 md:px-14 pt-6 pb-3 border-b border-white/5">
          <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.accent})` }}
              aria-hidden
            />
            {c.name} · Studio
          </div>
          <div className="hidden md:flex items-center gap-5 text-[11px] font-mono text-white/50 uppercase tracking-widest">
            <span>Servizi</span>
            <span>Metodo</span>
            <span>Case study</span>
            <span>Contatti</span>
          </div>
          <div
            className="text-[11px] font-mono px-3 py-1.5 rounded-full border border-white/15"
            style={{ color: p.primary }}
          >
            Prenota
          </div>
        </div>

        {/* Hero */}
        <div className="relative px-8 md:px-14 pt-10 pb-8">
          <p
            className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3"
            style={{ color: p.primary }}
          >
            {c.name.toUpperCase()} · {c.province} · {c.postalCode}
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h4
              className="font-semibold leading-[1.04] tracking-tight max-w-[18ch]"
              style={{
                fontSize: "clamp(26px, 3.4vw, 44px)",
                textWrap: "balance",
              }}
            >
              {s.shortLabel} a {c.name} che{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${p.primary}, ${p.accent})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                convertono
              </span>
            </h4>
            <p className="text-[13px] md:text-sm text-white/60 leading-relaxed max-w-[38ch]">
              {s.tagline}. {c.dominantIndustries[0] ? `Pensati per ${c.dominantIndustries[0]} e PMI della zona.` : ""}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            <span
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-[13px] font-medium text-slate-950"
              style={{
                background: `linear-gradient(135deg, ${p.primary}, ${p.accent})`,
              }}
            >
              Prenota consulenza
            </span>
            <span className="inline-flex items-center rounded-md border border-white/15 px-4 py-2 text-[13px] text-white/80">
              Come funziona →
            </span>
          </div>
        </div>

        {/* KPI strip */}
        <div className="relative mx-8 md:mx-14 mb-8 grid grid-cols-4 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {kpis.map((k, i) => (
            <div
              key={k.label}
              className="px-4 py-4 md:px-5 md:py-5"
              style={{
                borderRight: i < kpis.length - 1 ? "1px solid rgba(255,255,255,0.07)" : undefined,
              }}
            >
              <div
                className="font-display text-xl md:text-2xl font-semibold tracking-tight"
                style={{ color: p.primary }}
              >
                {k.value}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/45 mt-1">
                {k.label}
              </div>
            </div>
          ))}
        </div>

        {/* Feature cards */}
        <div className="relative mx-8 md:mx-14 mb-10 grid grid-cols-3 gap-3">
          {[s.processSteps[0], s.processSteps[1], s.processSteps[2]].map((step, i) => (
            <div
              key={i}
              className="rounded-lg border border-white/8 bg-white/[0.015] px-4 py-4"
            >
              <div
                className="text-[10px] font-mono uppercase tracking-wider mb-2"
                style={{ color: p.primary }}
              >
                0{i + 1}
              </div>
              <div className="text-[13px] font-medium text-white/90 leading-snug mb-1.5">
                {step.name}
              </div>
              <div className="text-[11px] text-white/50 leading-relaxed line-clamp-2">
                {step.text}
              </div>
            </div>
          ))}
        </div>

        {/* Footer strip */}
        <div className="relative px-8 md:px-14 py-4 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-white/35 uppercase tracking-widest">
          <span>
            Dario Marco Bellini · Bariano (BG)
          </span>
          <span style={{ color: p.primary }}>Dimensione 4 · 2026</span>
        </div>
      </div>

      <figcaption className="bg-[#0a0d14] border-t border-white/10 px-5 py-2.5 text-[11px] font-mono text-white/45 flex items-center justify-between">
        <span>Esempio parametrico · non un cliente reale · generato da Dimensione 4</span>
        <span>{variant}</span>
      </figcaption>
    </figure>
  );
}
